<template>
  <div class="flex flex-col gap-8">
    <!-- Field Sections -->
    <div class="c-field-sections-container">
      <template
        v-for="({ mdm_type, ids }, tableName, index) in sourceFieldIdsByMdm"
        :key="mdm_type"
      >
        <div class="c-field-section">
          <div class="c-field-section__header">
            {{ getSourceTableLabel(tableName, startCase(tableName)) }}
          </div>
          <BittsMultiselect
            :aria-label="`${getSourceTableLabel(tableName, startCase(tableName))} fields`"
            v-model="ids.value"
            :options="getFieldOptions(mdm_type)"
            mode="multiple"
            :disabled="disabled"
            :dropdown-button="true"
            custom-filter="label"
            placeholder="Select fields"
            class="c-field-section__multiselect"
          >
            <template #dropdownButton>
              <div
                class="bitts-multiselect-footer px-12 py-8 border-t border-neutral-border"
              >
                <BittsButton
                  size="x-small"
                  type="neutral"
                  text="Import more fields from your CRM"
                  :left-icon="['far', 'plus']"
                  class="w-full"
                  @click="$emit('navigate-to-sync-fields')"
                />
              </div>
            </template>
          </BittsMultiselect>
        </div>
        <BittsDivider
          v-if="index < Object.keys(sourceFieldIdsByMdm).length - 1"
          class="my-16"
        />
      </template>
    </div>

    <!-- Delete Preset Section (when editing a deletable preset) -->
    <div
      v-if="canDeletePreset"
      class="c-preset-save-section c-preset-delete-section"
    >
      <div class="flex items-center justify-between">
        <div class="flex flex-col gap-4">
          <h4 class="font-bold text-neutral-text-strong text-base m-0">
            Delete preset
          </h4>
          <p class="text-neutral-text text-sm m-0">
            Permanently delete this preset.
          </p>
        </div>
        <component
          :is="hasPartnersUsingPreset ? BittsTooltip : 'div'"
          placement="right"
          :mount-to-body="true"
        >
          <BittsButton
            text="Delete"
            type="danger"
            variant="outline"
            size="small"
            :left-icon="['far', 'trash']"
            :disabled="hasPartnersUsingPreset"
            @click="$emit('delete-preset')"
          />
          <template v-if="hasPartnersUsingPreset" #title>
            This preset is currently used by one or more populations. Update
            those populations before deleting it.
          </template>
        </component>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsDivider,
  BittsMultiselect,
  BittsTooltip,
} from '@crossbeam/bitts';

import { startCase } from 'lodash';
import { type Ref } from 'vue';

import { getSourceTableLabel } from '@/constants/data_sources';

type Props = {
  sourceFieldIdsByMdm: Record<
    string,
    {
      mdm_type: string;
      ids: Ref<number[]>;
    }
  >;
  getFieldOptions: (mdmType: string) =>
    | {
        label: string | null;
        value: number | undefined;
      }[]
    | undefined;
  disabled?: boolean;
  canDeletePreset?: boolean;
  hasPartnersUsingPreset?: boolean;
};

withDefaults(defineProps<Props>(), {
  disabled: false,
  canDeletePreset: false,
  hasPartnersUsingPreset: false,
});

defineEmits<{
  'delete-preset': [];
  'navigate-to-sync-fields': [];
}>();
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";

.c-field-sections-container {
  @apply bg-neutral-background-weak rounded-lg p-16;
}

.c-field-section {
  @apply flex items-start gap-16;
}

.c-field-section__header {
  @apply font-bold text-neutral-text-strong text-base flex-shrink-0 pt-8;
  min-width: 200px;
}

.c-field-section__multiselect {
  @apply flex-1;
}

.c-preset-save-section {
  @apply bg-neutral-background-weak rounded-lg p-16;
}
</style>
